import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { readImages } from '../../utils/files';
import type { StoredImage } from '../../utils/files';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [photos, setPhotos] = useState<StoredImage[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-89" data-mp="d8tgz43z76p5">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-90" data-mp="i2nyc0w3z9l2">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-91" data-mp="n0g0roudqe3i">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-92" data-mp="n2io2145dt4j">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-93" data-mp="r3m7exlem7ai">
              Fortæl om din opgave
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-94" data-mp="2s3r3cjppti9j">
              Beskriv kort hvad du skal have lavet. Du må meget gerne vedhæfte billeder, så er det nemmere at
              vurdere opgaven.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-95" data-mp="31vn3e0bd3ige">
              <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-96" data-mp="2x2ty9bbze83s">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-97" data-mp="36swy9vhismbm" />
                {settings.phone}
              </LinkButton>
              <LinkButton href={`mailto:${settings.email}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-98" data-mp="3kr3lmhouf3x1">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-99" data-mp="1qh00g2x3qg9g" />
                {settings.email}
              </LinkButton>
            </div>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-100" data-mp="ytci8v34ur5">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-101" data-mp="3ecxsc4i34yc6">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-102" data-mp="3jcaqhleepru2" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-103" data-mp="35cj7t7cy3n9q">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-104" data-mp="39fxnfwwz21mn">
                {settings.companyName} vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-105" data-mp="17mrocs1kv1zh">
                Send en opgave mere
              </Button>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={event => {
          event.preventDefault();
          if (submitting || uploading) return;
          if (!validate()) return;
          setSubmitting(true);
          const payload = {
            ...form,
            photos
          };
          window.setTimeout(() => {
            createEnquiry(payload);
            setForm(emptyForm);
            setPhotos([]);
            setErrors({});
            setSubmitting(false);
            setSent(true);
          }, 500);
        }} data-id="element-106" data-mp="dwhgcz4q8v0n">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-107" data-mp="3cewf9b1efbnt">
                Felter markeret med <span className="text-accent" data-id="element-108" data-mp="38crmeeiryj28">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-109" data-mp="38f5jesx0m5cf">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-110" data-mp="1ntz2pd3l3smt" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-111" data-mp="1479ouo44znmg" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="dig@mail.dk" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-112" data-mp="lg07jso4jukb" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Hvidovre" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-113" data-mp="s3yridrpwkpz" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-114" data-mp="9eieiqyuizso" />

              <div className="mt-5" data-id="element-115" data-mp="2xqmqnz3lq7qy">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-116" data-mp="2xtselb0hv3l3">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-117" data-mp="32s2axoxyga8a">
                  {uploading ? <Loader2Icon className="h-5 w-5 animate-spin text-ink-muted" aria-hidden="true" data-id="element-118" data-mp="3lhs1u0ylkbux" /> : <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-119" data-mp="37p2ent7c10w8" />}
                  <span className="text-[15px] font-medium text-ink" data-id="element-120" data-mp="3cn3v06ddim40">
                    {uploading ? 'Indlæser billeder …' : 'Vælg billeder'}
                  </span>
                  <span className="text-sm text-ink-muted" data-id="element-121" data-mp="90wthqcohqlx">Valgfrit — du kan tilføje flere ad gangen</span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" onChange={async event => {
                const input = event.target;
                setUploading(true);
                const added = await readImages(input.files);
                setPhotos(current => [...current, ...added]);
                setUploading(false);
                input.value = '';
              }} data-id="element-122" data-mp="253rujhatzxpq" />
                </label>

                {photos.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-123" data-mp="2a2ctldeckbcl">
                    {photos.map((photo, index) => <li key={`${photo.name}-${index}`} className="overflow-hidden rounded-xl border border-line" data-id="element-124" data-mp="12pau6aqqeb36" data-mpi={index}>
                        <div className="relative" data-id="element-125" data-mp="3abawz015b9tr">
                          <img src={photo.url} alt={photo.name} className="aspect-square w-full object-cover" data-id="element-126" data-mp="3a8gdpbtvbv1x" />
                          <button type="button" onClick={() => setPhotos(current => current.filter((_, i) => i !== index))} aria-label={`Fjern billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-127" data-mp="3o1efbfwwmjdx">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="27no840ahnf02" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-129" data-mp="2cnam4by54z9x">
                          <span className="truncate text-xs text-ink-muted" data-id="element-130" data-mp="2zfkcwo63v3bj">{photo.name}</span>
                          <button type="button" onClick={() => setPhotos(current => current.filter((_, i) => i !== index))} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-131" data-mp="34en7tfsdft5s">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-132" data-mp="wlsymohli64">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-133" data-mp="9xjrwqk2izhu" />
                  Tjek de markerede felter, før du sender.
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || uploading} data-id="element-134" data-mp="1ud9h0gbibdwc">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-135" data-mp="1gguve1013kzi" />
                    Sender …
                  </> : 'Send forespørgsel'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}